import { FormProvider as RHFFormProvider } from "react-hook-form";
import PropTypes from "prop-types";
import { Box, Button, Grid, CircularProgress, Alert } from "@mui/material";

const collectErrorMessages = (errors, messages = []) => {
  if (!errors || typeof errors !== "object") {
    return messages;
  }

  Object.values(errors).forEach((value) => {
    if (!value) return;
    if (typeof value.message === "string") {
      messages.push(value.message);
      return;
    }
    if (typeof value === "object") {
      collectErrorMessages(value, messages);
    }
  });

  return messages;
};

const FormProvider = ({ children, methods, onSubmit, onCancel, submitText, cancelText, isLoading, requireZodResolver = true }) => {
  const hasResolver = Boolean(methods?.control?._options?.resolver);
  if (requireZodResolver && !hasResolver) {
    throw new Error("FormProvider requires a resolver. Use Zod via useZodForm().");
  }

  const handleSubmit = methods.handleSubmit(onSubmit);
  const formErrors = methods.formState.errors || {};
  const formErrorMessages = collectErrorMessages(formErrors);

  return (
    <RHFFormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {formErrorMessages.map((errorMessage, index) => (
          <Alert key={`${errorMessage}-${index}`} severity="error" variant="outlined" sx={{ mb: 1 }}>
            {errorMessage}
          </Alert>
        ))}
        
        <Grid container spacing={2}>
          {children}
        </Grid>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          {!!onCancel && (
            <Button
              onClick={onCancel}
              color="inherit"
              disabled={isLoading}
              sx={{ mr: 1 }}
            >
              {cancelText}
            </Button>
          )}

          {!!onSubmit && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {submitText}
            </Button>
          )}
        </Box>
      </Box>
    </RHFFormProvider>
  );
};

FormProvider.propTypes = {
  children: PropTypes.node.isRequired,
  methods: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  submitText: PropTypes.string,
  cancelText: PropTypes.string,
  isLoading: PropTypes.bool,
  requireZodResolver: PropTypes.bool,
};

export default FormProvider;
